const BASE = '/api'

export class ApiError extends Error {
  status: number
  url: string
  code: string
  constructor(message: string, status: number, url: string, code = 'error') {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.url = url
    this.code = code
  }
}

/** いま見ている契約プロジェクト（すべての呼び出しに x-ail-project で付ける）。 */
let activeProject: string | null = null
export function setActiveProject(id: string | null): void {
  activeProject = id
}
export function getActiveProject(): string | null {
  return activeProject
}

/** ログインが切れた・二段階認証が要る時に知らせる。 */
type AuthListener = (code: string) => void
const authListeners = new Set<AuthListener>()
export function onAuthProblem(fn: AuthListener): () => void {
  authListeners.add(fn)
  return () => authListeners.delete(fn)
}

export type Query = Record<
  string,
  string | number | boolean | null | undefined | Array<string | number>
>

export function qs(params: Query = {}): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) {
      if (v.length === 0) continue
      sp.set(k, v.join(','))
    } else {
      sp.set(k, String(v))
    }
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

interface Options extends Omit<RequestInit, 'body'> {
  body?: unknown
  signal?: AbortSignal
}

/**
 * Throws on failure. TanStack Query owns retries, caching and error surfacing,
 * so this layer never swallows an error into a `{ data: null }` shape.
 */
export async function request<T>(path: string, opts: Options = {}): Promise<T> {
  const { body, headers, ...rest } = opts
  const url = `${BASE}${path}`
  let res: Response
  try {
    res = await fetch(url, {
      cache: 'no-store',
      ...rest,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'x-ail': '1',
        ...(activeProject ? { 'x-ail-project': activeProject } : {}),
        ...(body !== undefined && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : body instanceof FormData || typeof body === 'string' ? body : JSON.stringify(body),
    })
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e
    throw new ApiError('サーバーに接続できませんでした', 0, url)
  }

  if (!res.ok) {
    let detail = ''
    let code = 'error'
    try {
      const j = (await res.json()) as { detail?: string; error?: string | { code?: string; message?: string }; message?: string }
      if (j.error && typeof j.error === 'object') {
        detail = j.error.message ?? ''
        code = j.error.code ?? code
      } else {
        detail = j.detail || (j.error as string | undefined) || j.message || ''
      }
    } catch {
      detail = ''
    }
    if (res.status === 401 && (code === 'unauthorized' || code === 'mfa_required' || code === 'session_expired')) {
      for (const fn of authListeners) fn(code)
    }
    throw new ApiError(detail || `サーバーエラー (${res.status})`, res.status, url, code)
  }

  if (res.status === 204) return undefined as T
  const text = await res.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

export const api = {
  get: <T>(path: string, params?: Query, signal?: AbortSignal) =>
    request<T>(`${path}${qs(params)}`, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PUT', body, signal }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PATCH', body, signal }),
  del: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'DELETE', signal }),
  /** ファイルを送る（FormData）。 */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
}

/**
 * Reads a `text/event-stream`-ish NDJSON response line by line.
 * The submit endpoints stream progress this way.
 */
export async function streamJson(
  path: string,
  body: unknown,
  onEvent: (e: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', 'x-ail': '1', ...(activeProject ? { 'x-ail-project': activeProject } : {}) },
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok || !res.body) {
    throw new ApiError(`ストリームを開始できませんでした (${res.status})`, res.status, path)
  }
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    const lines = buf.split('\n')
    buf = lines.pop() ?? ''
    for (const raw of lines) {
      const line = raw.startsWith('data:') ? raw.slice(5).trim() : raw.trim()
      if (!line) continue
      try {
        onEvent(JSON.parse(line) as Record<string, unknown>)
      } catch {
        /* keep-alive or partial frame — ignore */
      }
    }
  }
}
